/**
 * CSI standard consent form templates (V1).
 * - standard_consentForm: generic eye-care informed consent (any clinic).
 * - standard_iplConsentForm / FollowUp: IPL-specific parity with the sample form.
 */

function yesNoPlain(label: string, sort: number) {
  return {
    label,
    required: false,
    typeConfig: {
      type: 'radio' as const,
      valuesConfig: {
        no: { label: 'No', sort: 1 },
        yes: { label: 'Yes', sort: 2 }
      }
    },
    sort
  };
}

function yesNoWithDetail(
  label: string,
  sort: number,
  supportingFieldId: string = 'yesDetail',
  detailRequired: boolean = true
) {
  return {
    label,
    required: false,
    typeConfig: {
      type: 'radio' as const,
      valuesConfig: {
        no: { label: 'No', sort: 1 },
        yes: {
          label: 'Yes',
          sort: 2,
          showSubField: true,
          subFieldIdList: [{ id: supportingFieldId, required: detailRequired }]
        }
      }
    },
    sort
  };
}

/** Nested acknowledgement cluster (content fields + trailing initials). */
function acknowledgementField(sort: number, formFields: { [key: string]: any }) {
  return {
    label: '',
    typeConfig: {
      type: 'acknowledgement' as const,
      formFields
    },
    sort
  };
}

function initialsAcknowledgement(
  initialsKey: string,
  label: string,
  sort: number,
  required: boolean = true
) {
  return acknowledgementField(sort, {
    [initialsKey]: {
      label,
      required,
      typeConfig: { type: 'initials' as const, maxLength: 10 },
      sort: 10
    }
  });
}

/** Generic eye-care informed consent — not treatment- or device-specific. */
export const standard_consentForm = {
  name: 'Standard Consent',
  id: 'standard_consentForm',
  type: 'consent',
  message:
    'Please read each statement carefully, initial where indicated, and sign below to provide your informed consent.',
  abbreviation: 'Consent',
  attribution: 'CSI Standard Consent Form V1',
  availableLanguages: [{ id: 'en', label: 'English' }],
  supportingFields: {},
  templateGroups: {
    introduction: {
      title: 'Introduction',
      nextLabel: 'Next',
      sort: 1,
      formFields: {
        introLegalText: {
          label: 'Introduction',
          typeConfig: {
            type: 'legalText',
            content:
              '<p><strong>Please read each statement carefully and initial where indicated.</strong></p><p>This form records your informed consent for eye care examination and/or treatment at this clinic. By completing it, you acknowledge that outcomes vary between individuals, that no result can be guaranteed, and that your eye care practitioner will discuss recommended care, alternatives, and material risks with you as appropriate.</p>'
          },
          sort: 100
        },
        consentDate: {
          label: 'Date',
          required: true,
          typeConfig: {
            type: 'date',
            showNotApplicableOption: false,
            maxDateToday: true,
            lockedToToday: true
          },
          sort: 200
        }
      }
    },
    acknowledgements: {
      title: 'Acknowledgements',
      nextLabel: 'Next',
      sort: 2,
      formFields: {
        ackAuthorize: initialsAcknowledgement(
          'ackAuthorizeInitials',
          'I authorize this clinic and its eye care practitioners to examine and/or treat me for my ocular and related health conditions.',
          100
        ),
        ackRisks: initialsAcknowledgement(
          'ackRisksInitials',
          'I understand that examination and treatment involve potential benefits and risks, which have been or will be explained to me by my eye care practitioner.',
          200
        ),
        ackQuestions: initialsAcknowledgement(
          'ackQuestionsInitials',
          'I have had, or will have, the opportunity to ask questions about my care and to have them answered to my satisfaction.',
          300
        ),
        ackNoGuarantee: initialsAcknowledgement(
          'ackNoGuaranteeInitials',
          'I understand that no guarantee can be made regarding the outcome of any examination, diagnosis, or treatment, and that results vary between individuals.',
          400
        ),
        ackInstructions: initialsAcknowledgement(
          'ackInstructionsInitials',
          'I agree to provide accurate information about my health history and to follow the care instructions provided by my eye care practitioner.',
          500
        ),
        ackRecordPhotos: initialsAcknowledgement(
          'ackRecordPhotosInitials',
          'I consent to clinical photographs or imaging being taken for my medical record to document my condition and progress.',
          600
        ),
        ackEducationPhotos: initialsAcknowledgement(
          'ackEducationPhotosInitials',
          'I consent to de-identified photographs being used for medical education or publication, with discretion and without revealing my identity.',
          700,
          false
        ),
        ackWithdraw: initialsAcknowledgement(
          'ackWithdrawInitials',
          'I understand that I may withdraw consent for future care at any time, and that declining recommended care may affect my outcomes.',
          800
        )
      }
    },
    signatures: {
      title: 'Consent and signature',
      nextLabel: 'Review',
      sort: 3,
      formFields: {
        consentCertification: {
          label: 'Certification',
          typeConfig: {
            type: 'legalText',
            content:
              '<p>My signature certifies that I have read and understood this informed consent form. I hereby freely consent to examination and/or treatment by this eye care practice.</p>'
          },
          sort: 100
        },
        patientPrintName: {
          label: 'Name of patient (please print)',
          required: true,
          typeConfig: { type: 'input', multiLine: false },
          sort: 200
        },
        patientSignature: {
          label: 'Signature of patient',
          required: true,
          typeConfig: { type: 'signature' },
          sort: 300
        },
        patientSignDate: {
          label: 'Date',
          required: true,
          typeConfig: {
            type: 'date',
            showNotApplicableOption: false,
            maxDateToday: true,
            lockedToToday: true
          },
          sort: 400
        },
        witnessPrintName: {
          label: 'Name of witness (please print)',
          required: false,
          typeConfig: { type: 'input', multiLine: false },
          sort: 500
        },
        witnessSignature: {
          label: 'Signature of witness',
          required: false,
          typeConfig: { type: 'signature' },
          sort: 600
        },
        witnessSignDate: {
          label: 'Date',
          required: false,
          typeConfig: {
            type: 'date',
            showNotApplicableOption: false,
            maxDateToday: true,
            lockedToToday: true
          },
          sort: 700
        }
      }
    }
  }
};

export const standard_iplConsentForm = {
  name: 'IPL Treatment Consent',
  id: 'standard_iplConsentForm',
  type: 'consent',
  message:
    'Please read and initial each statement. Complete, underline or circle individual selection accordingly.',
  abbreviation: 'IPL Consent',
  attribution: 'CSI Standard Consent Form V1',
  availableLanguages: [{ id: 'en', label: 'English' }],
  supportingFields: {
    conditionOtherDetail: {
      label: 'Please specify other condition',
      required: true,
      typeConfig: { type: 'input', multiLine: false }
    },
    yesDetail: {
      label: 'Please specify',
      required: true,
      typeConfig: { type: 'input', multiLine: false }
    },
    yesWhatWhenDetail: {
      label: 'What / when?',
      required: true,
      typeConfig: { type: 'input', multiLine: false }
    },
    medicationListDetail: {
      label: 'List of medications',
      required: true,
      typeConfig: { type: 'input', multiLine: true }
    }
  },
  templateGroups: {
    introduction: {
      title: 'Introduction',
      nextLabel: 'Next',
      sort: 1,
      formFields: {
        introLegalText: {
          label: 'Introduction',
          typeConfig: {
            type: 'legalText',
            content:
              '<p><strong>Please read and initial each statement. Complete, underline or circle individual selection accordingly.</strong></p><p>By doing so you are agreeing that you have been made aware that there is no guarantee that this treatment will resolve all of issues you present with and there is a chance that there could be no improvement to symptoms and that patient outcomes vary from one individual to the next.</p>'
          },
          sort: 100
        },
        treatmentDate: {
          label: 'Date',
          required: true,
          typeConfig: {
            type: 'date',
            showNotApplicableOption: false,
            maxDateToday: true,
            lockedToToday: true
          },
          sort: 200
        }
      }
    },
    acknowledgements: {
      title: 'Acknowledgements',
      nextLabel: 'Next',
      sort: 2,
      formFields: {
        ack1: acknowledgementField(100, {
          ack1Text: {
            label: 'Practitioner and condition',
            typeConfig: {
              type: 'legalText',
              content:
                '<p>I authorise practitioner to perform IPL treatments on me in an effort to improve the condition(s) selected below.</p>'
            },
            sort: 100
          },
          practitionerName: {
            label: 'Practitioner name',
            required: true,
            clinicianOnly: true,
            typeConfig: { type: 'input', multiLine: false },
            sort: 110
          },
          treatmentConditions: {
            label: 'Condition(s) to be treated',
            required: true,
            clinicianOnly: true,
            typeConfig: {
              type: 'checkbox',
              columnType: 'double',
              valuesConfig: {
                mgd: { label: 'Meibomian Gland Dysfunction (MGD)', sort: 1 },
                ded: { label: 'Dry Eye Disease (DED)', sort: 2 },
                rosacea: { label: 'Rosacea / ocular rosacea', sort: 3 },
                telangiectasia: { label: 'Telangiectasia', sort: 4 },
                other: {
                  label: 'Other',
                  sort: 5,
                  showSubField: true,
                  subFieldIdList: [{ id: 'conditionOtherDetail', required: true }]
                }
              }
            },
            sort: 120
          },
          ack1Initials: {
            label: '',
            required: true,
            typeConfig: { type: 'initials', maxLength: 10 },
            sort: 130
          }
        }),
        ack2: initialsAcknowledgement(
          'ack2Initials',
          'I understand that there is a rare possibility of side effects or serious complications including permanent discoloration and scarring. I am aware that careful adherence to all advised instructions will help reduce this possibility',
          200
        ),
        ack3: acknowledgementField(300, {
          ack3Text: {
            label: 'Short-term effects',
            typeConfig: {
              type: 'legalText',
              content:
                '<p>I understand the below list of short-term effects and agree to follow matching guidelines:</p>'
            },
            sort: 300
          },
          shortTermEffectFlaking: {
            label: '',
            required: true,
            typeConfig: {
              type: 'checkbox',
              columnType: 'single',
              valuesConfig: {
                flaking: {
                  label:
                    'Flaking of pigmented lesions – crusts may take 5 to 10 days to disappear and it is important not to manipulate or pick which may otherwise lead to scarring',
                  sort: 1
                }
              }
            },
            sort: 310
          },
          shortTermEffectDiscomfort: {
            label: '',
            required: true,
            typeConfig: {
              type: 'checkbox',
              columnType: 'single',
              valuesConfig: {
                discomfort: {
                  label:
                    'Discomfort – during the procedure, I might experience a sensation similar to a rubber band snap which degree will vary per my skin condition and area sensitivity but that does not last long. A mild “sun-burn” sensation may follow for typically up to one hour and will be reduced with application of cooling and soothing creams. Reddening and swelling – severity and duration depend on the intensity of the treatment and the sensitivity of the area to be treated. These phenomena may be reduced with application of cooling and/or anti-inflammatory creams',
                  sort: 1
                }
              }
            },
            sort: 320
          },
          shortTermEffectBruising: {
            label: '',
            required: true,
            typeConfig: {
              type: 'checkbox',
              columnType: 'single',
              valuesConfig: {
                bruising: {
                  label: 'Bruising may rarely occur and may last up to 2 weeks',
                  sort: 1
                }
              }
            },
            sort: 330
          },
          ack3Initials: {
            label: '',
            required: true,
            typeConfig: { type: 'initials', maxLength: 10 },
            sort: 340
          }
        }),
        ack4: initialsAcknowledgement(
          'ack4Initials',
          'I understand that sun exposure or tanning of any sort is not aligned with the pre and/or post-care instructions and may increase the chance for complications',
          400
        ),
        ack5: initialsAcknowledgement(
          'ack5Initials',
          'The procedure as well as potential benefits and risks have been thoroughly explained to me and I have had all my related questions answered',
          500
        ),
        ack6: initialsAcknowledgement(
          'ack6Initials',
          'Pre and post-care instructions have been discussed and are completely clear to me',
          600
        ),
        ack7: initialsAcknowledgement(
          'ack7Initials',
          'I understand that results may vary with each individual and acknowledge that it is impossible to predict how I will respond to the treatment and how many sessions will be required',
          700
        ),
        ack8: initialsAcknowledgement(
          'ack8Initials',
          'I consent to photographs being taken for the purpose of documenting my progress and response to the treatment and be kept solely in my medical record',
          800
        ),
        ack9: initialsAcknowledgement(
          'ack9Initials',
          'I consent to photographs being used for medical education or publication with applied discretion and not revealing my identity',
          900,
          false
        ),
        ack10: initialsAcknowledgement(
          'ack10Initials',
          'I agree to review the following IPL pre-treatment compliance checklist along with my Physician and bring accurate and updated data, to the best of my knowledge',
          1000
        )
      }
    },
    medicalHistory: {
      title: 'Medical history',
      nextLabel: 'Next',
      sort: 3,
      formFields: {
        medicalHistoryOptionalNote: {
          label: 'Medical history note',
          typeConfig: {
            type: 'legalText',
            content:
              '<p>Please complete the following medical history to the best of your knowledge. You may leave items blank and review them with your Physician at the time of treatment.</p>'
          },
          sort: 50
        },
        skinType: {
          label: 'Skin type of the area to be treated:',
          required: false,
          typeConfig: {
            type: 'radio',
            valuesConfig: {
              I: { label: 'I', sort: 1 },
              II: { label: 'II', sort: 2 },
              III: { label: 'III', sort: 3 },
              IV: { label: 'IV', sort: 4 },
              V: { label: 'V', sort: 5 },
              VI: { label: 'VI', sort: 6 }
            }
          },
          sort: 100
        },
        sunAndSelfTanners: yesNoPlain(
          'Natural or artificial sun exposure in the past 3-4 weeks pre-op or the following 3-4 weeks post-op plan. Use of self-tanners or tan enhancer caps within the past 3-4 weeks pre-op plan',
          200
        ),
        photosensitiveHerbs: yesNoWithDetail(
          "Photosensitive herbal preparations (St John's Wort, Ginkgo Biloba, etc...) or aromatherapy (essential oils)",
          300
        ),
        lightStimulatedDisease: yesNoWithDetail(
          'Diseases which may be stimulated by light at 400 nm to 1200 nm, such as history of Systemic Lupus Erythematosus or Porphyria',
          400
        ),
        pregnancyNursing: yesNoPlain(
          'Pregnant or possibility of pregnancy, postpartum or nursing',
          500
        ),
        inflammatorySkin: yesNoWithDetail('Inflammatory skin conditions (dermatitis, etc...)', 600),
        coldSoresHerpes: yesNoPlain(
          'Presence or history of active cold sores or herpes simplex virus',
          700
        ),
        // Key must not match DERFS nonModifiable allowlist (`hiv`) — defense in depth.
        consentHiv: yesNoPlain('HIV', 800),
        activeCancer: yesNoPlain('Active cancer (currently on chemotherapy or radiation)', 900),
        previousSkinCancer: yesNoPlain('Previous skin cancer?', 1000),
        keloids: yesNoPlain('Medical history of keloids', 1100),
        isotretinoin: yesNoPlain('Intake of isotretinoin within the past year', 1200),
        koebnerizingDiseases: yesNoPlain(
          'Medical history of Koebnerizing isomorphic diseases (vitiligo, psoriasis)',
          1300
        ),
        knownAllergy: yesNoWithDetail('Any known allergy?', 1400),
        tattooOrPigmentedLesion: yesNoWithDetail(
          'Any tattoo and/or pigmented lesion on requested treatment area that should be protected?',
          1500
        ),
        additionalMedications: yesNoWithDetail(
          'List of additional current medication taken',
          1600,
          'medicationListDetail'
        ),
        hormonalEndocrineDisorders: yesNoWithDetail(
          'Hormonal or endocrine disorders (PCOS or uncontrolled diabetes?)',
          1700
        ),
        previousHairRemoval: yesNoWithDetail(
          'Previous hair removal procedures on requested treatment area (other IPL/laser, wax, electrolysis, etc....)',
          1800,
          'yesWhatWhenDetail'
        ),
        lesionModification: yesNoWithDetail(
          'Any observed modification (colour, size, texture and border) on the lesion to be treated?',
          1900
        ),
        hairShouldNotBeRemoved: yesNoPlain(
          'Any hair on requested treatment area that should not be removed?',
          2000
        ),
        lesionOnsetAge: {
          label: 'Answer',
          required: false,
          typeConfig: {
            type: 'input',
            multiLine: false,
            title: 'Age of lesion onset?'
          },
          sort: 2100
        },
        previousSkinProcedures: yesNoWithDetail(
          'Previous skin procedures on requested treatment area (Botox, fillers, peels, etc...)',
          2200,
          'yesWhatWhenDetail'
        ),
        aspirinAnticoagulants: yesNoWithDetail('Intake of aspirin or anti-coagulants?', 2300),
        easyBruising: yesNoPlain('Easy bruising?', 2400)
      }
    },
    signatures: {
      title: 'Consent and signature',
      nextLabel: 'Review',
      sort: 4,
      formFields: {
        consentCertification: {
          label: 'Certification',
          typeConfig: {
            type: 'legalText',
            content:
              '<p>My signature certifies that I have duly read and understood the content of this informed consent form, and gave the accurate information as to my health condition. I hereby freely consent to IPL skin treatments.</p>'
          },
          sort: 100
        },
        patientPrintName: {
          label: 'Name of patient (please print)',
          required: true,
          typeConfig: { type: 'input', multiLine: false },
          sort: 200
        },
        patientSignature: {
          label: 'Signature of patient',
          required: true,
          typeConfig: { type: 'signature' },
          sort: 300
        },
        patientSignDate: {
          label: 'Date',
          required: true,
          typeConfig: {
            type: 'date',
            showNotApplicableOption: false,
            maxDateToday: true,
            lockedToToday: true
          },
          sort: 400
        },
        witnessPrintName: {
          label: 'Name of witness (please print)',
          required: false,
          typeConfig: { type: 'input', multiLine: false },
          sort: 500
        },
        witnessSignature: {
          label: 'Signature of witness',
          required: false,
          typeConfig: { type: 'signature' },
          sort: 600
        },
        witnessSignDate: {
          label: 'Date',
          required: false,
          typeConfig: {
            type: 'date',
            showNotApplicableOption: false,
            maxDateToday: true,
            lockedToToday: true
          },
          sort: 700
        }
      }
    }
  }
};

export const standard_iplConsentFollowUp = {
  name: 'IPL Follow-up Consent',
  id: 'standard_iplConsentFollowUp',
  type: 'consent',
  message:
    'Confirm there have been no health changes affecting your suitability for IPL since your last visit, then sign.',
  abbreviation: 'IPL Follow-up Consent',
  attribution: 'CSI Standard Consent Form V1',
  availableLanguages: [{ id: 'en', label: 'English' }],
  supportingFields: {},
  templateGroups: {
    declaration: {
      title: 'Follow-up declaration',
      nextLabel: 'Sign',
      sort: 1,
      formFields: {
        followUpDeclaration: acknowledgementField(100, {
          followUpLegalText: {
            label: 'Declaration',
            typeConfig: {
              type: 'legalText',
              content:
                '<p><strong>I confirm that there have been no changes to my health that would affect my suitability for IPL treatment since my last visit.</strong></p>'
            },
            sort: 100
          },
          followUpInitials: {
            label: '',
            required: true,
            typeConfig: { type: 'initials', maxLength: 10 },
            sort: 110
          }
        }),
        followUpPrintName: {
          label: 'Print name',
          required: true,
          typeConfig: { type: 'input', multiLine: false },
          sort: 300
        },
        followUpSignature: {
          label: 'Signature',
          required: true,
          typeConfig: { type: 'signature' },
          sort: 400
        },
        followUpDate: {
          label: 'Date',
          required: true,
          typeConfig: {
            type: 'date',
            showNotApplicableOption: false,
            maxDateToday: true,
            lockedToToday: true
          },
          sort: 500
        }
      }
    }
  }
};
